import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useEmbajadorImpersonation, usePublicarVerComoEmbajador } from "@/contexts/EmbajadorImpersonationContext";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { Button } from "@/components/ui/button";
import { ImpersonationViewModeBanner } from "@/components/admin/ImpersonationViewModeToggle";
import { EmbajadorImpersonationSelector } from "./EmbajadorImpersonationSelector";
import { PortalEmbajadorNavDesktop, PortalEmbajadorNavMobile } from "./PortalEmbajadorNav";

// Título de la sección activa. El portal no tenía ninguno: el encabezado solo
// decía "Portal / Embajadores SOZU" en 10px, así que no se sabía dónde estabas.
const SECTION_TITLE: Record<string, string> = {
  inicio: "Inicio",
  "mis-referidos": "Mis referidos",
  "registrar-referido": "Registrar referido",
  comisiones: "Comisiones",
  pagos: "Pagos",
  perfil: "Perfil",
  inventario: "Inventario",
};

export const PortalEmbajadorLayout = () => {
  // Solo mientras este layout está montado se ve como el usuario (y en solo lectura).
  usePublicarVerComoEmbajador();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const { impersonatedEmbajadorName, isImpersonating } = useEmbajadorImpersonation();

  // Quién puede impersonar lo dice `roles.puede_impersonar`, no una lista de ids.
  // El hardcode `rol_id === 1 || 2` dejaba a Admin Soporte (30) sin selector, y
  // sin selector caía en el guard "no estás ligado a un embajador".
  const canImpersonate = profile?.puede_impersonar === true;
  const { canReturnToAdmin } = useCanReturnToAdmin();

  const displayName = isImpersonating
    ? impersonatedEmbajadorName
    : profile?.nombre || "Embajador";

  const sectionTitle =
    SECTION_TITLE[location.pathname.replace(/\/+$/, "").split("/").pop() ?? ""] ?? "Portal";

  const fotoVista = useFotoPerfilVista();

  return (
    <PortalTrackingProvider portal="embajadores">
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-30 h-14 border-b bg-card/80 backdrop-blur flex items-center justify-between px-4 lg:px-6 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:block">
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide leading-none">
              Portal Embajadores
            </div>
            <div className="text-base font-bold leading-tight tracking-tight">{sectionTitle}</div>
          </div>
          <div className="sm:hidden text-sm font-bold leading-tight">{sectionTitle}</div>
        </div>
        <div className="flex items-center gap-2">
          {canReturnToAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              className="gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Volver al admin</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => signOut()}
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Cerrar sesión</span>
          </Button>
          {/* "Ver como" a la derecha, justo antes del nombre y el avatar. */}
          {canImpersonate && (
            <div className="hidden sm:flex">
              <EmbajadorImpersonationSelector />
            </div>
          )}
          <div className="text-right hidden md:block pl-2 border-l">
            <div className="text-sm font-medium leading-tight">{displayName}</div>
            <div className="text-xs text-muted-foreground leading-tight">
              {isImpersonating ? "Vista embajador" : profile?.rol_nombre}
            </div>
          </div>
          <AvatarPersona nombre={displayName} fotoUrl={fotoVista} size="sm" />
        </div>
      </header>

      {canImpersonate && (
        <div className="sm:hidden border-b bg-card/60 px-4 py-2">
          <EmbajadorImpersonationSelector />
        </div>
      )}

      {isImpersonating && <ImpersonationViewModeBanner targetName={impersonatedEmbajadorName} />}

      {/* pb-24 en móvil para que la barra flotante no tape el contenido. */}
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-4 pb-24 sm:px-6 md:pb-4 lg:px-8">
        <div className="mb-6">
          <PortalEmbajadorNavDesktop />
        </div>
        <Outlet />
      </main>

      <PortalEmbajadorNavMobile />
    </div>
    </PortalTrackingProvider>
  );
};
